import shortid from 'shortid'
import loremIpsum from 'lorem-ipsum'
import defaultsDeep from 'lodash/fp/defaultsDeep'
import { markdown } from 'markdown'
import _ from 'lodash/fp'

function camelToKababCase(camel) {
  return camel.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()
}

export class RenderElement {
  constructor(attributes = {}) {
    this.attributes = attributes
  }

  style(initial = '', attributes = null) {
    let attrs = attributes || this.attributes

    return Object.entries(attrs).reduce((acc, [name, cur]) => {
      let val = cur.value.trim()
      // ignore empty values
      if (val && val.length) {
        acc += `${name}:${val} !important;`
      }
      return acc
    }, initial)
  }

  toJSON(key) {
    return Object.assign(this, { '__TYPE__': this.constructor.name })
  }

  classes(initial = '') {
    return initial
  }

  render() {
    return ''
  }
}

export class LayoutContainer extends RenderElement {
  constructor(children = [], attributes = {}) {
    super(defaultsDeep({
      'padding': { value: '0px', label: 'Padding' },
      'background-color': { value: 'transparent', label: 'Background Color' }
      // 'background-image':      { value: '',            label: 'Background Image'      },
      // 'background-position-x': { value: '',            label: 'Background Position X' },
      // 'background-position-y': { value: '',            label: 'Background Position Y' },
      // 'background-size':       { value: '',            label: 'Background Size'       },
    }, attributes))

    this.id = shortid.generate()
    this.component = 'LayoutContainer'

    this.children = children
  }

  // hoist global classes
  classes(initial = '') {
    return this.children.reduce((acc, child) => {
      let res = child.classes()
      // return `${acc}\n${res}`
      return acc + res
    }, initial)
  }

  render() {
    let html = `
      <table data-${camelToKababCase(this.component)} width="100%">
    `

    html = this.children.reduce((acc, child) => {
      acc += `
        <tr>
          <td style="${this.style()}">
      `
      acc += child.render()
      acc += `
          </td>
        </tr>
      `

      return acc
    }, html)

    html += `
      </table>
    `

    return html
  }
}

export class DocumentContainer extends LayoutContainer {
  constructor(header = new DocumentSection(), body = new DocumentSection(), footer = new DocumentSection()) {
    super([header, body, footer], {})

    this.component = 'DocumentContainer'

    this.cssClass = shortid.generate()
  }

  get header() { return this.children[0] }
  set header(val) { this.children[0] = val }

  get body() { return this.children[1] }
  set body(val) { this.children[1] = val }

  get footer() { return this.children[2] }
  set footer(val) { this.children[2] = val }

  toJSON(key) {
    return Object.assign(this, { '__TYPE__': this.constructor.name })
  }

  render() {
    // based on Cerberus responsive template https://github.com/TedGoas/Cerberus/blob/master/cerberus-responsive.html
    let html = `
      <!DOCTYPE html>
      <html lang="en" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width">
          <meta http-equiv='X-UA-Compatible' content="IE=edge">
          <meta name="x-apple-disabled-message-reformatting">
          <title></title>

          <!--[if mso]>
            <style>
              * { font-family: sans-serif !important; }
            </style>
          <![endif]-->

          <!--[if !mso]><!-->
            <link href='https://fonts.googleapis.com/css?family=Roboto:400,700' rel='stylesheet' type='text/css'>
          <!--<![endif]-->

          <!-- CSS reset -->
          <style>
            html, body {
              margin: 0 auto !important;
              padding: 0 !important;
              height: 100% !important;
              width: 100% !important;
            }

            * {
              -ms-text-size-adjust: 100%;
              -webkit-text-size-adjust: 100%;
            }

            div[style*="margin: 16px 0"] {
              margin: 0 !important;
            }

            table, td {
              mso-table-lspace: 0pt !important;
              mso-table-rspace: 0pt !important;
            }

            table {
              border-spacing: 0 !important;
              border-collapse: collapse !important;
              table-layout: fixed !important;
              margin: 0 auto !important;
            }
            table table table {
              table-layout: auto;
            }

            a {
              text-decoration: none;
            }

            img {
              -ms-interpolation-mode:bicubic;
            }

            *[x-apple-data-detectors], /* iOS */
            .unstyle-auto-detected-links *,
            .aBn {
              border-bottom: 0 !important;
              cursor: default !important;
              color: inherit !important;
              text-decoration: none !important;
              font-size: inherit !important;
              font-family: inherit !important;
              font-weight: inherit !important;
              line-height: inherit !important;
            }

            .im {
              color: inherit !important;
            }

            .a6S {
              display: none !important;
              opacity: 0.01 !important;
            }

            img.g-img + div {
               display: none !important;
            }

            /* iPhone 4, 4S, 5, 5S, 5C, and 5SE */
            @media only screen and (min-device-width: 320px) and (max-device-width: 374px) {
              u ~ div .email-container {
                min-width: 320px !important;
              }
            }
            /* iPhone 6, 6S, 7, 8, and X */
            @media only screen and (min-device-width: 375px) and (max-device-width: 413px) {
              u ~ div .email-container {
                min-width: 375px !important;
              }
            }
            /* iPhone 6+, 7+, and 8+ */
            @media only screen and (min-device-width: 414px) {
              u ~ div .email-container {
                min-width: 414px !important;
              }
            }
          </style>

          <!--[if mso]>
            <style type="text/css">
              ul, ol {
                margin: 0 !important;
              }
              li {
                margin-left: 30px !important;
              }
              li.list-item-first {
                margin-top: 0 !important;
              }
              li.list-item-last {
                margin-bottom: 10px !important;
              }
            </style>
          <![endif]-->

          <!-- content styles -->
          <style>
            .content_${this.cssClass} * {
              box-sizing: border-box !important;
            }

            .content_${this.cssClass} blockquote {
              box-sizing: border-box !important;
              padding: 20px !important;
              margin-left: 20px !important;
              margin-right: 20px !important;
              border-top: solid 2px #DDD !important;
              border-bottom: solid 2px #DDD !important;
            }

            .content_${this.cssClass} hr {
              background: #CCCCCC !important;
              border: 0 !important;
              height: 1px !important;
            }
          </style>

          <!-- global classes -->
          <style>
            ${this.classes()}
          </style>

        </head>

        <body width="100%" style="margin: 0; padding: 0 !important; mso-line-height-rule: exactly; background-color: #FFFFFF;">
          <!-- preheader -->
          <div style="display: none; font-size: 1px; line-height: 1px; max-height: 0px; max-width: 0px; opacity: 0; overflow: hidden; mso-hide: all; font-family: sans-serif;">
          </div>

          <div style="display: none; font-size: 1px; line-height: 1px; max-height: 0px; max-width: 0px; opacity: 0; overflow: hidden; mso-hide: all; font-family: sans-serif;">
            &zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;
          </div>

          <table data-${camelToKababCase(this.component)} width="100%" class="content_${this.cssClass}">
    `
    html = this.children.reduce((acc, child) => {
      acc += `
        <tr>
          <td style="${this.style()}">
      `
      acc += child.render()
      acc += `
          </td>
        </tr>
      `

      return acc
    }, html)

    html += `
          </table>
        </body>
      </html>
    `

    return html
  }
}

export class DocumentSection extends LayoutContainer {
  constructor(children = [], attributes = {}) {
    super(children, attributes)

    this.component = 'DocumentSection'
  }

  render() {
    let html = `
      <table data-${camelToKababCase(this.component)} width="100%">
        <tr>
          <td align="center" style="${this.style()}">
    `

    html = this.children.reduce((acc, child) => {
      acc += child.render()
      return acc
    }, html)

    html += `
          </td>
        </tr>
      </table>
    `

    return html
  }
}

export class RowContainer extends LayoutContainer {
  constructor(children = [], attributes = {}) {
    super(children, attributes)

    this.component = 'RowContainer'
  }

  render() {
    let html = `
      <table data-${camelToKababCase(this.component)} width="100%" style="max-width: 600px;">
    `

    html = this.children.reduce((acc, child) => {
      acc += `
        <tr>
          <td style="${this.style()}">
      `
      acc += child.render()
      acc += `
          </td>
        </tr>
      `

      return acc
    }, html)

    html += `
      </table>
    `

    return html
  }
}

export class ColumnContainer extends LayoutContainer {
  constructor(children = [], attributes = {}, fields = []) {
    super(children, defaultsDeep({
      'width': { value: '100%', label: 'Width' },
      'height': { value: '', label: 'Height' },
      'text-align': { value: 'left',
        label: 'Align',
        type: 'select',
        options: [
          { value: 'left', label: 'Left' },
          { value: 'center', label: 'Center' },
          { value: 'right', label: 'Right' },
          { value: 'justify', label: 'Justify' }
        ] },
      'vertical-align': { value: 'top',
        label: 'Vertical Align',
        type: 'select',
        options: [
          { value: 'top', label: 'Top' },
          { value: 'middle', label: 'Middle' },
          { value: 'bottom', label: 'Bottom' }
        ] },
      'padding': { value: '10px', label: 'Padding' },
      'background-color': { value: 'transparent', label: 'Background Color' },
      'background-image': { value: '', label: 'Background Image', type: 'file' },
      'background-position-x': { value: '', label: 'Background Position X' },
      'background-position-y': { value: '', label: 'Background Position Y' },
      'background-size': { value: '', label: 'Background Size' },
      'background-repeat': { value: 'no-repeat', label: 'Background Repeat' },
      'color': { value: '#000', label: 'Text Color' }
    }, attributes))

    this.component = 'ColumnContainer'
  }

  style() {
    let initialStyles = `
      background-repeat: no-repeat;
    `

    // text-align and td align are incompatible so remove text-align from styles
    let attrs = _.omit([
      'text-align',
      'vertical-align',
      'background-position-x',
      'background-position-y'
    ], this.attributes)

    let backgroundX = this.attributes['background-position-x'] ? this.attributes['background-position-x'].value : ''
    let backgroundY = this.attributes['background-position-y'] ? this.attributes['background-position-y'].value : ''

    attrs['background-position'] = { value: `${backgroundX} ${backgroundY}` }
    return super.style(initialStyles, attrs)
  }

  render() {
    let html = `
      <table data-${camelToKababCase(this.component)} width="100%">
    `

    html = this.children.reduce((acc, child) => {
      acc += `
        <tr>
          <td
            width="${this.attributes.width.value}"
            align="${this.attributes['text-align'].value}"
            valign="${this.attributes['vertical-align'].value}"
            style="${this.style()}"
          >
      `
      acc += child.render()
      acc += `
          </td>
        </tr>
      `

      return acc
    }, html)

    html += `
      </table>
    `

    return html
  }
}

export class LayoutContent extends RenderElement {
  constructor(attributes = {}) {
    super(attributes)
  }
}

export class ContentImage extends LayoutContent {
  constructor(url = '') {
    super({ 'width': { value: '100%', label: 'Width' } })

    this.component = 'ContentImage'
    this.url = url
  }

  render() {
    return `<img data-${camelToKababCase(this.component)} src="${this.url}" style="${this.style()}" />`
  }
}

export class ContentText extends LayoutContent {
  constructor(text = loremIpsum({ count: 5 })) {
    super()
    this.component = 'ContentText'
    this.text = text
    this.markup = 'html' // text, html, markdown
  }

  render() {
    let html = this.markup === 'markdown' ? markdown.toHTML(this.text) : this.text
    return `<span data-${camelToKababCase(this.component)}>${html}</span>`
  }
}

export class ContentHorizontalRule extends LayoutContent {
  constructor() {
    super()
    this.component = 'ContentHorizontalRule'
  }

  render() {
    return `<hr data-${camelToKababCase(this.component)} style="${this.style()}" />`
  }
}

export class ContentSpacer extends LayoutContent {
  constructor() {
    super({
      'width': { value: '50px', label: 'Width' },
      'height': { value: '50px', label: 'Height' }
    })

    this.component = 'ContentSpacer'
  }

  render() {
    return `<div data-${camelToKababCase(this.component)} style="${this.style()}"></div>`
  }
}

export class ContentButton extends LayoutContent {
  constructor(text = 'Button Text') {
    super({
      'width': { value: 'auto', label: 'Width' },
      'padding': { value: '20px', label: 'Padding' },
      'background-color': { value: 'rebeccapurple', label: 'Background Color' },
      'border-width': { value: '0', label: 'Border Width' },
      'border-color': { value: '', label: 'Border Color' },
      'border-radius': { value: '5px', label: 'Border Radius' },
      'color': { value: '#FFF', label: 'Text Color' },

      'text': { value: 'Button Text', label: text },
      'url': { value: 'http://', label: 'Button Link URL' }
    })

    this.component = 'ContentButton'

    this.cssClass = shortid.generate()
  }

  get text() { return (this.attributes.text && this.attributes.text.value) || '' }
  set text(val) { this.attributes.text.value = val }

  get url() { return (this.attributes.url && this.attributes.url.value) || '' }
  set url(val) { this.attributes.url.value = val }

  style() {
    let initialStyles = `
      display: block;
      border-style: solid;
      text-align: center;
    `

    let attrs = _.omit([
      'text',
      'url'
    ], this.attributes)

    return super.style(initialStyles.replace(/\s+/g, ''), attrs)
  }

  classes() {
    return super.classes(`
      .${this.cssClass}, .${this.cssClass}:hover, .${this.cssClass} a, .${this.cssClass} a:hover {
        ${this.style()}
      }
    `)
  }

  render() {
    return `
      <a data-${camelToKababCase(this.component)} href="${this.url}" target="_blank" style="text-decoration: none;" class="${this.cssClass}" styles="${this.style()}">${this.text}</a>
    `
  }
}

export class ContentSocial extends LayoutContent {
  constructor(networks = [
    { id: 'facebook', name: 'Facebook', user: 'ding', url: 'https://facebook.com/ding', iconUrl: 'icon-facebook.png' },
    { id: 'twitter', name: 'Twitter', user: '@ding', url: 'https://twitter.com/ding', iconUrl: 'icon-twitter.png' },
    { id: 'instagram', name: 'Instagram', user: 'ding', url: 'https://instagram.com/ding', iconUrl: 'icon-instagram.png' }
  ]) {
    super()

    this.component = 'ContentSocial'

    this.networks = networks

    // TODO: these will not stay connected to the values on the object unless getters and setters are used
    this.fields = networks.reduce((acc, network) => {
      acc[`social.${network.id}.url`] = { value: network.url, label: `${network.name} URL` }
      acc[`social.${network.id}.user`] = { value: network.user, label: `${network.name} User` }
      return acc
    }, {})

    this.iconBaseUrl = process.env.VUE_APP_SHARED_MEDIA_BASE_URL

    this.attributes = _.merge(this.fields, {
      'child.spacing': { value: '10px', label: 'Spacing' },
      'icon.size': { value: '60px', label: 'Icon Size' }
    })
  }

  render() {
    let html = `
      <table data-${camelToKababCase(this.component)} style="${this.style()}">
        <tr>
    `

    html = this.networks.reduce((acc, network) => {
      let url = this.attributes[`social.${network.id}.url`].value
      let user = this.attributes[`social.${network.id}.user`].value
      let iconWidth = this.attributes['icon.size'].value

      let iconUrl = `${this.iconBaseUrl}${network.iconUrl}`

      if (url.length) {
        acc += `
          <td style="${this.childStyle()}">
            <a href="${url}" target="_blank" title="${user}"><img src="${iconUrl}" style="${this.iconStyle()}" width="${iconWidth}" alt="${user}"></a>
          </td>
        `
      }

      return acc
    }, html)

    html += `
        </tr>
      </table>
    `

    return html
  }

  style() {
    // strip any namespaced attributes (anything with a dot in it)
    let attrs = _.omit(Object.keys(this.attributes).filter(attr => !attr.includes('.')), this.attributes)
    return super.style('', attrs)
  }

  childStyle() {
    return `
      padding-left: ${this.attributes['child.spacing'].value};
      padding-right: ${this.attributes['child.spacing'].value};
    `
  }

  iconStyle() {
    return ''
  }
}
